// src/clock/outbox.js — offline write queue for clock in/out (v124.10).
//
// Pay-path reliability. Today a clock_in/clock_out that fails because the DEVICE
// is OFFLINE (patchy store signal) is surfaced as an error and LOST — the staffer
// thinks they clocked in but the DB has nothing, so their pay is wrong. This queues
// that event locally and replays it when the connection returns.
//
// SAFE TO REPLAY: the clock write is an idempotent UPSERT on (staff_id, date) with a
// version-controlled unique index (migrations v134-0 + v135-0). Re-sending the same
// event rewrites the same row — it can NEVER create a duplicate. We keep ONE entry per
// (staff, date), so a later clock-out naturally supersedes an earlier queued clock-in,
// matching the server's upsert.
//
// ONLY OFFLINE FAILURES ARE QUEUED: db.js distinguishes a network failure (DB.isOnline()
// goes false) from a server rejection (the server responded 4xx/5xx). We queue only the
// former — a server "no" still surfaces as an error, never silently swallowed.
//
// SECURITY: a queued entry NEVER contains the PIN. db.js re-attaches live auth
// (authFields() from the in-memory cache) at REPLAY time, so no credential is ever parked
// on the device. If auth isn't available at replay (cache expired / app reopened), the
// item stays queued and drains once the staffer next verifies their PIN (which they do to
// use the clock anyway).
//
// SCOPE (v1): the clock_in/clock_out event save only. Breaks and stuck-session recovery
// still save directly (unchanged) — a follow-up can route them through here too.

const OUTBOX_KEY = 'clock_outbox_v1';
// Dead-letter store for entries the SERVER permanently rejects (a "poison" entry — a payload
// the upsert can never accept). Without this, drain() retried such an entry forever, blocking
// the whole queue and showing "waiting to sync" that never clears (the v124.26 Marina Mall
// report). After MAX_REPLAY_ATTEMPTS server rejections an entry is MOVED here (NOT deleted —
// the clock data is preserved) and surfaced so the staffer re-enters it.
const DEAD_KEY = 'clock_outbox_dead_v1';
const MAX_REPLAY_ATTEMPTS = 10;   // server rejections before an entry is treated as poison

// ── Pure helpers (exported for tests) ──────────────────────────────────────

// One queued entry per (staff, date, seq) — matches the server's
// (staff_id, date, seq) upsert. v129.158: seq joined the key, else a queued
// second session would overwrite the queued first in the outbox itself and a
// split day worked offline would lose its morning before ever reaching the
// server. seq defaults to 1, so every pre-split entry keys exactly as before.
export function outboxKey(entry) {
  return String((entry && entry.staff_id) != null ? entry.staff_id : '') + '|' +
         String((entry && entry.date) || '') + '|' +
         String((entry && entry.seq) || 1);
}

// Strip client-only queue metadata (the `_`-prefixed fields we stamp locally, e.g.
// `_queued_at`) BEFORE replay — they are not clock_log columns and the worker rejects the
// insert (PGRST204) if they reach it. Pure; returns a clean copy. staff_id/date remain, so
// outboxKey still resolves.
export function stripQueueMeta(entry) {
  const out = {};
  Object.keys(entry || {}).forEach(function (k) {
    if (k.charCodeAt(0) === 95) return;   // drop any `_`-prefixed client-only field
    out[k] = entry[k];
  });
  return out;
}

// Strip auth (and anything sensitive) BEFORE persisting — only the clock data is stored.
export function stripForQueue(payload) {
  const out = {};
  Object.keys(payload || {}).forEach(function (k) {
    if (k === 'auth_pin' || k === 'auth_staff_id') return;   // never persist a credential
    out[k] = payload[k];
  });
  return out;
}

// Insert/replace by key, keeping the NEWEST entry for a (staff, date). Pure — returns a
// new array.
export function upsertQueue(list, entry) {
  const key = outboxKey(entry);
  const next = (Array.isArray(list) ? list : []).filter(function (e) { return outboxKey(e) !== key; });
  next.push(entry);
  return next;
}

// ── localStorage persistence (the app already relies on localStorage for offline) ──

function read() {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; }
}
// v136.8: write() says whether it STORED the list. It used to swallow a full device's
// QuotaExceededError, so saveClock reported "Saved offline" for a clock event that was never
// kept anywhere (pay data lost, staffer told it was safe). On a refused write, the per-view
// offline caches are cleared (DB.freeCacheSpace, the same rescue cacheSet uses) and the write
// is tried once more.
function write(list) {
  const v = JSON.stringify(list || []);
  try { localStorage.setItem(OUTBOX_KEY, v); return true; } catch (e) { /* full: free space below */ }
  try {
    if (typeof DB !== 'undefined' && DB && typeof DB.freeCacheSpace === 'function') DB.freeCacheSpace();
  } catch (e) { /* best-effort */ }
  try { localStorage.setItem(OUTBOX_KEY, v); return true; } catch (e) { return false; }
}

export function pending() { return read(); }
export function pendingCount() { return read().length; }

function enqueue(payload) {
  const entry = stripForQueue(payload);
  entry._queued_at = Date.now();
  return write(upsertQueue(read(), entry));
}
function removeKey(key) {
  write(read().filter(function (e) { return outboxKey(e) !== key; }));
}
// Re-read, increment the _attempts counter on the matching entry, persist. Returns the new
// count. Read-modify-write (not a stale-snapshot mutation) so it composes with removeKey.
function bumpAttempts(key) {
  let n = 0;
  const next = read().map(function (e) {
    if (outboxKey(e) === key) { e._attempts = (e._attempts || 0) + 1; n = e._attempts; }
    return e;
  });
  write(next);
  return n;
}
// Move a permanently-rejected ("poison") entry to the dead-letter store (bounded). The clock
// data is KEPT so nothing is silently lost; the staffer is told to re-enter it.
function deadLetter(entry) {
  try {
    let list = [];
    try { const a = JSON.parse(localStorage.getItem(DEAD_KEY)); if (Array.isArray(a)) list = a; } catch (e) {}
    list.push(Object.assign({}, entry, { _dead_at: Date.now() }));
    while (list.length > 20) list.shift();   // never grow unbounded
    localStorage.setItem(DEAD_KEY, JSON.stringify(list));
  } catch (e) { /* best-effort */ }
}
// v125.71 (Marina Mall: "still there is message at the top… could not be saved")
// — dead-lettered entries previously lived FOREVER, so the ⚠ re-enter banner
// never cleared even after the staffer re-entered the shift. Two escape hatches:
// an explicit dismiss (below) and this age-based expiry, so an ignored banner
// stops nagging once the entry is past any realistic re-entry window.
const DEAD_MAX_AGE_MS = 14 * 24 * 3600 * 1000;   // 14 days

export function pendingDead() {
  try {
    const a = JSON.parse(localStorage.getItem(DEAD_KEY));
    if (!Array.isArray(a)) return [];
    const now = Date.now();
    const live = a.filter(function (e) {
      return e && (typeof e._dead_at !== 'number' || (now - e._dead_at) < DEAD_MAX_AGE_MS);
    });
    if (live.length !== a.length) {
      try { localStorage.setItem(DEAD_KEY, JSON.stringify(live)); } catch (e2) {}
    }
    return live;
  } catch (e) { return []; }
}
export function deadLetterCount() { return pendingDead().length; }

// The staffer's "✓ I've re-entered it" — clears the dead-letter store and the
// banner. Explicit user action (never automatic), so nothing pay-affecting is
// hidden without a human saying it's handled.
export function dismissDead() {
  try { localStorage.removeItem(DEAD_KEY); } catch (e) {}
  updateIndicator();
}

// ── The resilient save — call this instead of DB.clock.save ────────────────

// Resolves with the server result on success, or { queued:true, offline:true } when the
// device was offline and the event was safely queued. Re-throws a genuine server rejection
// (4xx/5xx) or an auth prompt — those are NOT queueable offline failures.
export async function saveClock(payload) {
  try {
    const res = await DB.clock.save(payload);
    // v132.96 (overnight audit, approved by Matt 29 Sep 2026): a direct save
    // that succeeds is the NEWEST word on that person's session, so any older
    // queued entry for the same (staff, date, seq) must not replay over it
    // later (a stale queued clock-in could wipe a newer clock-out).
    try {
      const key = outboxKey(payload);
      if (read().some(function (e) { return outboxKey(e) === key; })) { removeKey(key); updateIndicator(); }
    } catch (e2) { /* best-effort; the save itself succeeded */ }
    return res;
  } catch (e) {
    const offline = (typeof DB !== 'undefined' && typeof DB.isOnline === 'function' && DB.isOnline() === false);
    if (offline) {
      // v136.8: only claim "queued" when the entry was really stored. A device whose storage
      // is full gets the error path instead ("Clock save failed"), never a false "Saved offline".
      if (!enqueue(payload)) {
        updateIndicator();
        const err = new Error('Offline and this device has no room to keep the clock event');
        err.storageFull = true;
        throw err;
      }
      updateIndicator();
      return { queued: true, offline: true };
    }
    throw e;
  }
}

// ── Replay ─────────────────────────────────────────────────────────────────

let _draining = false;

// Replay queued clock events. Stops at the first failure (offline again, or auth not yet
// available) — the rest stay queued for the next trigger. Idempotent upsert makes re-sends
// safe. Never runs two drains at once.
export async function drain() {
  if (_draining) return { drained: 0, remaining: pendingCount() };
  // Overnight fix (sensitive): with no PIN cached, DB.clock.save goes through
  // _withAuthRetry, which pops the staff picker on the till - every 60 seconds
  // from the safety-net interval below, with nobody having asked. The header
  // has always promised the opposite: entries just stay queued until someone
  // next verifies a PIN. So skip the pass quietly (no attempt, no penalty).
  if (typeof DB !== 'undefined' && typeof DB.hasAuthPin === 'function' && !DB.hasAuthPin()) {
    updateIndicator();
    return { drained: 0, deadLettered: 0, remaining: pendingCount(), skipped: 'no-auth' };
  }
  _draining = true;
  let drained = 0;
  let deadLettered = 0;
  try {
    const list = read();
    for (let i = 0; i < list.length; i++) {
      const entry = list[i];
      const key = outboxKey(entry);
      try {
        await DB.clock.save(stripQueueMeta(entry));  // drop _queued_at etc; re-attaches live auth via authFields()
        removeKey(key);
        drained++;
      } catch (e) {
        // Distinguish an OFFLINE/network failure (the whole queue is blocked by the network —
        // leave everything, retry when back; do NOT penalise the entry) from a SERVER
        // rejection (the server responded "no"). Same test db.js uses for saveClock.
        const offline = (typeof DB !== 'undefined' && typeof DB.isOnline === 'function' && DB.isOnline() === false);
        if (offline) break;
        // v128.25 (re-audit): a definite 5xx is the SERVER struggling (DB
        // outage, deploy blip) — not a verdict on this entry. With the 60s
        // replay interval, a ~10-minute outage answering 500s used to burn
        // all MAX_REPLAY_ATTEMPTS and dead-letter VALID pay entries. A 5xx
        // now behaves like offline: stop the pass, no penalty, retry later.
        // Only a definite 4xx (db.js attaches e.status) — or a status-less
        // legacy error, which keeps the Marina-Mall poison protection —
        // counts toward the cap.
        if (e && typeof e.status === 'number' && e.status >= 500) break;
        // v132.96: a refusal about WHO is asking is not a verdict on the entry.
        // On a shared till the cached PIN is the last person who typed one, so
        // replaying A's queued clock-in under B's PIN gets 403 ("Cannot act on
        // behalf of another staff"). That used to count, and after ten tries
        // A's clock-in was dead-lettered. Now: no penalty, move on to the next
        // entry (it may be B's own), and A's drains when A next enters a PIN.
        // v133.86 (overnight sweep, L): but a 401 is a WRONG PIN (the cached one is stale, e.g. reset), and each
        // wrong PIN counts towards the person's lockout and this till's: carrying on would spend one per
        // queued entry. Stop the pass; it drains with the next good PIN. A 403 (right PIN, someone else's
        // entry) carries on as before.
        if (e && e.status === 401) break;
        if (e && e.status === 403) continue;
        // No PIN cached at all: stop this pass, no penalty (the header comment
        // always promised this; a status-less error used to count instead).
        if (e && /^Authentication required/.test(String(e.message || ''))) break;
        // Server responded with an error → count an attempt against THIS entry.
        const attempts = bumpAttempts(key);
        if (attempts >= MAX_REPLAY_ATTEMPTS) {
          // Poison: permanently rejected. Move to the dead-letter store, drop it from the
          // active queue, and CONTINUE draining the rest — one bad entry must not block them
          // or show "waiting to sync" forever (the Marina Mall report).
          deadLetter(entry);
          removeKey(key);
          deadLettered++;
          continue;
        }
        break;   // under the cap → stop this pass; retry next trigger (gives a transient blip time to clear)
      }
    }
  } finally {
    _draining = false;
    updateIndicator();
  }
  return { drained: drained, deadLettered: deadLettered, remaining: pendingCount() };
}

// ── UI indicator ("N clock events waiting to sync") ────────────────────────

function updateIndicator() {
  try {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('clockSyncPending');
    if (!el) return;
    // A dead-lettered (permanently-rejected) entry takes priority — tell the staffer to
    // re-enter it rather than showing "waiting to sync" forever. v125.71: name the DATES
    // (so they know WHAT to re-enter) + a dismiss button (so the banner can actually
    // clear once it's handled — it used to nag forever, the Marina Mall report).
    const deadList = pendingDead();
    if (deadList.length > 0) {
      const dates = deadList.map(function (e) { return (e && e.date) || '?'; }).join(', ');
      el.style.display = 'block';
      el.innerHTML = '⚠ ' + deadList.length + ' clock ' + (deadList.length === 1 ? 'entry' : 'entries') +
        ' (' + dates.replace(/&/g, '&amp;').replace(/</g, '&lt;') + ")" +
        " couldn't be saved. Please re-enter " + (deadList.length === 1 ? 'it' : 'them') + ' or tell your manager. ' +
        '<button onclick="Clock.dismissDead()" style="margin-inline-start:6px;border:none;border-radius:10px;padding:3px 10px;' +
        'background:var(--brand,#009E3D);color:#fff;font-size:12px;cursor:pointer">✓ I\'ve re-entered ' +
        (deadList.length === 1 ? 'it' : 'them') + '</button>';
      return;
    }
    const n = pendingCount();
    if (!n) { el.style.display = 'none'; el.textContent = ''; return; }
    el.style.display = 'block';
    el.textContent = '↻ ' + n + ' clock ' + (n === 1 ? 'entry' : 'entries') +
      " waiting to sync. They will send automatically when you're back online.";
  } catch (e) { /* indicator is best-effort; never break the clock */ }
}

// ── Init — wire the replay triggers once (called from the app boot) ────────

// v124.13: show/hide the "you are offline" strip on the clock screen so a staffer knows
// WHY a save might be pending — pairs with the queue (anything they clock is kept + synced).
// Best-effort + DOM-guarded; never breaks the clock.
function updateOfflineStrip() {
  try {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('clockOfflineStrip');
    if (!el) return;
    const offline = (typeof navigator !== 'undefined' && navigator.onLine === false);
    el.style.display = offline ? 'block' : 'none';
  } catch (e) { /* best-effort */ }
}

let _inited = false;
// Safe to call on every clock-screen render: it always refreshes the indicators, and
// wires the replay/online triggers exactly once (guarded).
export function initOutbox() {
  updateIndicator();                       // refresh "N waiting to sync" on every call
  updateOfflineStrip();                    // refresh the offline strip on every call
  if (_inited || typeof window === 'undefined') return;
  _inited = true;
  const kick = function () { updateOfflineStrip(); drain(); };
  window.addEventListener('online', kick);
  window.addEventListener('offline', updateOfflineStrip);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') kick();
    });
  }
  // Safety net in case an 'online' event is missed on some devices.
  setInterval(function () { if (navigator.onLine !== false && pendingCount()) drain(); }, 60000);
  if (navigator.onLine !== false && pendingCount()) drain();
}
